import React from "react";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import { DataRowExternalLink, DataRow } from "@ui/DataRow";
import { Icon } from "@ui/Icon";
import { HeadlineImage, HeadlineImageProps } from "@ui/HeadlineImage";
import { messages } from "../../messages";
import { Playlist as Model } from "../../models";
import { Badge, Button } from "reactstrap";

interface Props {
    playlist: Model;
    style?: "headline";
    link?: boolean;
    children?: React.ReactNode;
    additionalDetails?: boolean;
}

export const Playlist = ({ playlist, style, children, link, additionalDetails }: Props) => {
    // Build the info.
    const info: JSX.Element[] = [];
    let className = "playlist";
    let TitleTag = "span";
    if (style === "headline") {
        className += " playlist--headline";
        TitleTag = "h2";
    }
    const imageProps: HeadlineImageProps = { src: null };
    if (playlist != null) {
        imageProps.src = playlist.getMediumImage();
        imageProps.alt = playlist.name;
        if (style === "headline" && playlist.vendor != null) {
            info.push(
                <h5 key="format">
                    <Badge color="secondary">{playlist.getVendorName()}</Badge>
                </h5>,
            );
        }
        let title = React.createElement(
            TitleTag,
            { key: "title", className: "playlist__title" },
            playlist.name,
        );
        const url = playlist.getViewUrl();
        if (url != null && link) {
            imageProps.to = url;
            title = <Link key="title" to={url}>{title}</Link>;
        }
        info.push(title);
        if (additionalDetails) {
            if (playlist.description) {
                info.push(
                    <p key="description">{playlist.description}</p>,
                );
            }
            if (playlist.meta != null && playlist.meta.owner != null && playlist.meta.owner.name != null) {
                if (playlist.meta.owner.url != null) {
                    info.push(
                        <DataRowExternalLink
                            key="owner"
                            message={messages.playlistColumnMetaOwner}
                            value={playlist.meta.owner.name}
                            to={playlist.meta.owner.url}
                        />,
                    );
                }
                else {
                    info.push(
                        <DataRow
                            key="owner"
                            message={messages.playlistColumnMetaOwner}
                            value={playlist.meta.owner.name}
                        />,
                    );
                }
            }
            if (playlist.url != null) {
                const linkProps = {
                    key: "link",
                    tag: "a" as React.ReactType,
                    href: playlist.url,
                    target: "_blank",
                    outline: true,
                };
                info.push(
                    <br key="newline" />,
                    <Button {...linkProps}>
                        <Icon name="play" />{" "}
                        <FormattedMessage
                            {...messages.playlistListenOn}
                            values={{ vendor: playlist.getVendorName() }}
                        />
                    </Button>,
                );
            }
        }
    }
    return (
        <div className={className}>
            <div className="playlist__artwork">
                <HeadlineImage {...imageProps} />
            </div>
            <div className="playlist__info">{info}</div>
            <div className="playlist__children">{children}</div>
        </div>
    );
};
